import * as React from "react";
import { PlayerCampaign } from "@/interfaces";
import { URLS } from "@/utils";
import { CampaignServices } from "@/services";
import { Item, PaginationSimple, Spinner } from "@/components/ui";
import { BadgeCheckIcon, ChevronRightIcon, MegaphoneIcon } from "lucide-react";
import { FormCard } from "@/components";

interface Props {
  playerId: number;
  handleCancelAction?: () => void;
}

export function PlayerCampaigns({ playerId }: Props) {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [playerCampaigns, setPlayerCampaigns] = React.useState<PlayerCampaign[]>([]);
  const [totalPages, setTotalPages] = React.useState<number>(0);
  const [page, setPage] = React.useState<number>(1);

  React.useEffect(() => {
    if (!playerId) return;
    getPlayerCampaigns(page);
  }, [playerId, page]);

  const getPlayerCampaigns = (pageToFetch: number): void => {
    setLoading(true);
    CampaignServices.playerCampaigns(playerId, pageToFetch - 1, 6)
      .then((response: any) => {
        setPlayerCampaigns(response.content);
        setTotalPages(response.totalPages ?? 1);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <FormCard>
      <FormCard.Header title={"Campaigns"} description={"Active Campaigns that this player is currently enrolled in"} />
      <FormCard.Content>
        {loading && <Spinner />}

        {/* Populated State */}
        {!loading && playerCampaigns.length > 0 && (
          <div className="space-y-2">
            {playerCampaigns.map(c => (
              <Item variant="outline" size="sm" key={`camp-${c.campaignId}`}>
                <a
                  href={URLS.coach.editCampaigns.replace(":campaignId", c.campaignId.toString())}
                  className="flex flex-row items-center justify-between gap-2 w-full"
                >
                  <Item.Content className="flex flex-row items-center gap-2">
                    <BadgeCheckIcon className="size-5" />
                    <Item.Title>{c.campaignName}</Item.Title>
                  </Item.Content>
                  <Item.Actions>
                    <ChevronRightIcon className="size-4" />
                  </Item.Actions>
                </a>
              </Item>
            ))}
          </div>
        )}

        {/* Zero State */}
        {!loading && playerCampaigns.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
              <MegaphoneIcon className="size-6 text-slate-500 dark:text-slate-400" />
            </div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">No active campaigns</h3>
            <p className="mt-1 max-w-sm text-sm text-slate-500 dark:text-slate-400">
              This player isn't enrolled in any campaigns yet. Head over to the campaigns dashboard to assign them
              content.
            </p>
            <div className="mt-6">
              <a
                href={URLS.coach.campaigns}
                className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm"
              >
                Go to Campaigns
              </a>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4">
            <PaginationSimple page={page} totalPages={totalPages} onPageChange={setPage} disabled={loading} />
          </div>
        )}
      </FormCard.Content>
    </FormCard>
  );
}
